import React, { useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Area, XAxis, YAxis, ResponsiveContainer, Tooltip, AreaChart } from 'recharts'
import { AutoRow, RowBetween } from 'components/Row'
import { toK, toNiceDate, toNiceDateYear, formattedNum, getTimeframe } from 'utils/infos'
import { OptionButton } from '../ButtonStyled'
import { darken } from 'polished'
import { useMedia } from 'react-use'
import { TimeframeOptions } from 'constants/index'
import DropdownSelect from '../DropdownSelect'
import { useUserLiquidityChart } from 'hooks/infos/User'
import LocalLoader from 'components/Loading'
import { useDarkModeManager } from 'state/user/hooks'
import { TYPE } from 'theme'

const ChartWrapper = styled.div`
  height: 100%;
  max-height: 390px;

  @media screen and (max-width: 600px) {
    min-height: 200px;
  }
`

const UserChart = ({ account }: { account: string }) => {
  const { t } = useTranslation()
  const chartData = useUserLiquidityChart(account)

  const [timeWindow, setTimeWindow] = useState(TimeframeOptions.ALL_TIME)
  let utcStartTime = getTimeframe(timeWindow)

  const below600 = useMedia('(max-width: 600px)')
  const above1600 = useMedia('(min-width: 1600px)')

  const domain: any[] = [(dataMin: number) => (dataMin > utcStartTime ? dataMin : utcStartTime), 'dataMax']

  const aspect = above1600 ? 60 / 12 : below600 ? 60 / 42 : 60 / 16

  const [darkMode] = useDarkModeManager()
  const textColor = darkMode ? 'white' : 'black'

  return (
    <ChartWrapper>
      {below600 ? (
        <RowBetween mb={40}>
          <div />
          <DropdownSelect options={TimeframeOptions} active={timeWindow} setActive={setTimeWindow} color={'#623391'} />
        </RowBetween>
      ) : (
        <RowBetween mb={40}>
          <AutoRow gap="10px">
            <TYPE.v2Main>{t('analytics.liquidity_value')}</TYPE.v2Main>
          </AutoRow>
          <AutoRow justify="flex-end" gap="4px">
            <OptionButton
              active={timeWindow === TimeframeOptions.MONTH}
              onClick={() => setTimeWindow(TimeframeOptions.MONTH)}
            >
              {t('analytics.1m')}
            </OptionButton>
            <OptionButton
              active={timeWindow === TimeframeOptions.WEEK}
              onClick={() => setTimeWindow(TimeframeOptions.WEEK)}
            >
              {t('analytics.1w')}
            </OptionButton>
            <OptionButton
              active={timeWindow === TimeframeOptions.ALL_TIME}
              onClick={() => setTimeWindow(TimeframeOptions.ALL_TIME)}
            >
              {t('analytics.all')}
            </OptionButton>
          </AutoRow>
        </RowBetween>
      )}
      {chartData ? (
        <ResponsiveContainer aspect={aspect}>
          <AreaChart margin={{ top: 0, right: 10, bottom: 6, left: 0 }} barCategoryGap={1} data={chartData}>
            <defs>
              <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={'#623391'} stopOpacity={0.35} />
                <stop offset="95%" stopColor={'#623391'} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              tickLine={false}
              axisLine={false}
              interval="preserveEnd"
              tickMargin={16}
              minTickGap={0}
              tickFormatter={tick => toNiceDate(tick)}
              dataKey="date"
              tick={{ fill: textColor }}
              type={'number'}
              domain={domain}
            />
            <YAxis
              type="number"
              orientation="right"
              tickFormatter={tick => '$' + toK(tick)}
              axisLine={false}
              tickLine={false}
              interval="preserveEnd"
              minTickGap={6}
              yAxisId={0}
              tick={{ fill: textColor }}
            />
            <Tooltip
              cursor={true}
              formatter={(val: number) => formattedNum(val, true)}
              labelFormatter={label => toNiceDateYear(label)}
              labelStyle={{ paddingTop: 4 }}
              contentStyle={{
                padding: '10px 14px',
                borderRadius: 10,
                borderColor: '#623391',
                color: 'black'
              }}
              wrapperStyle={{ top: -70, left: -10 }}
            />
            <Area
              key={'other'}
              dataKey={'valueUSD'}
              stackId="2"
              strokeWidth={2}
              dot={false}
              type="monotone"
              name={'Liquidity'}
              yAxisId={0}
              stroke={darken(0.12, '#623391')}
              fill="url(#colorUv)"
            />
          </AreaChart>
        </ResponsiveContainer>
      ) : (
        <LocalLoader show={true} />
      )}
    </ChartWrapper>
  )
}

export default UserChart
